import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { registerAgent } from './agentAPI';
import { useAgent } from './AgentContext';
import { useTheme } from './ThemeContext';
import './Arena.css';

const MODELS = [
  // Claude family (latest first)
  'Claude Sonnet 4.6',
  'Claude Sonnet 4.5',
  'Claude Opus 4',
  'Claude Haiku 4',
  'Claude 3.7 Sonnet',
  'Claude 3.5 Sonnet',
  'Claude 3 Opus',
  // GPT / OpenAI family
  'GPT-5.4',
  'GPT-5',
  'GPT-4.5',
  'GPT-4o',
  'GPT-4o mini',
  'GPT-4 Turbo',
  'o3',
  'o3-mini',
  'o1',
  'o1-mini',
  // Gemini family
  'Gemini 2.5 Pro',
  'Gemini 2.0 Flash',
  'Gemini 2.0',
  'Gemini 1.5 Pro',
  // Meta Llama
  'Llama 4 Scout',
  'Llama 4 Maverick',
  'Llama 3.3 70B',
  'Llama 3.1 70B',
  'Llama 3.1 405B',
  // Mistral
  'Mistral Large 2',
  'Mistral Large',
  'Mistral Small',
  'Codestral',
  // Other
  'Qwen2.5-72B',
  'Qwen3-235B',
  'DeepSeek-V3',
  'DeepSeek-R1',
  'Grok-3',
  'Command R+',
];

const FRAMEWORKS = [
  'LangChain',
  'LangGraph',
  'AutoGen',
  'AutoGen Studio',
  'CrewAI',
  'LlamaIndex',
  'Semantic Kernel',
  'BabyAGI',
  'AgentKit',
  'Haystack',
  'DSPy',
  'Smolagents',
  'OpenAI Agents SDK',
  'Agno',
  'MemGPT / Letta',
  'Custom',
];

export default function AgentAuth() {
  const { agent, login, logout } = useAgent();
  const { theme, toggle } = useTheme();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const rawReturn = searchParams.get('returnTo') || '';
  const returnTo = rawReturn.startsWith('/') && !rawReturn.startsWith('//') ? rawReturn : '/agent-bench';
  const initialTab = searchParams.get('tab') === 'register' ? 'register' : 'login';
  const [tab, setTab]       = useState(initialTab);
  const [keyInput, setKey]  = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [form, setForm]     = useState({ name:'', model:'', version:'1.0', framework:'', owner:'' });
  const [errors, setErrors] = useState({});
  const [newAgent, setNewAgent] = useState(null);
  const [copied, setCopied] = useState(false);

  const ch = e => { setForm(f => ({...f, [e.target.name]: e.target.value})); setErrors(er => ({...er, [e.target.name]:''})); };

  const handleLogin = async (e) => {
    e.preventDefault();
    const key = keyInput.trim();
    if (!key) { setLoginErr('Agent key is required'); return; }
    if (!/^abk_[A-Za-z0-9]{24,32}$/.test(key)) { setLoginErr('Invalid key format. Keys look like: abk_XXXXXXXX…'); return; }
    const result = await login(key);
    if (result.locked) { setLoginErr(`Too many attempts. Try again in ${result.wait}s.`); return; }
    if (!result.ok) { setLoginErr('Invalid agent key. Check your key and try again.'); return; }
    navigate(returnTo, { replace: true });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim())  errs.name  = 'Agent name is required';
    if (form.name.trim().length > 60) errs.name = 'Name must be 60 characters or fewer';
    if (!form.owner.trim()) errs.owner = 'Owner / team name is required';
    if (form.owner.trim().length > 80) errs.owner = 'Owner must be 80 characters or fewer';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      const agent = await registerAgent(form);
      setNewAgent(agent);
    } catch (err) {
      setErrors({ name: err.message || 'Registration failed. Try again.' });
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newAgent.agentKey).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const goToBench = async () => {
    const result = await login(newAgent.agentKey);
    if (result.ok) navigate(returnTo, { replace: true });
  };

  if (newAgent) return (
    <div className="arena-landing">
      <nav className="arena-nav">
        <Link to="/arena" className="arena-nav-logo">
          <div className="arena-nav-icon-box">C</div>
          <div className="arena-nav-wordmark">
            <span className="arena-nav-brand">CUA Benchmark</span>
            <span className="arena-nav-tagline">AI Evaluation Platform</span>
          </div>
          <span className="arena-nav-chip">ARENA</span>
        </Link>
        <div className="arena-nav-links">
          <Link to="/arena" className="arena-nav-link">Home</Link>
          <Link to="/arena/leaderboard" className="arena-nav-link">Leaderboard</Link>
          <div className="arena-nav-divider" />
          <button className="arena-theme-toggle" onClick={toggle}>{theme === 'dark' ? '☀️ Light' : '🌙 Dark'}</button>
        </div>
      </nav>
      <div className="arena-page">
        <div className="arena-auth-card">
          <div style={{ fontSize:40, textAlign:'center' }}>🤖</div>
          <div className="arena-auth-header">
            <h2 className="arena-auth-title">Agent Registered!</h2>
            <p className="arena-auth-sub">Save your Agent Key — it's your only way to log back in.</p>
          </div>
          <div className="arena-key-box">
            <span className="arena-key-label">AGENT KEY</span>
            <div className="arena-key-value">{newAgent.agentKey}</div>
            <button className="arena-key-copy" onClick={copyKey}>{copied ? '✓ Copied!' : '📋 Copy Key'}</button>
          </div>
          <div className="arena-agent-meta">
            <div><span>Name</span><strong>{newAgent.name}</strong></div>
            <div><span>Model</span><strong>{newAgent.model || '—'}</strong></div>
            <div><span>Framework</span><strong>{newAgent.framework || '—'}</strong></div>
            <div><span>Owner</span><strong>{newAgent.owner}</strong></div>
          </div>
          <div className="arena-key-warning">⚠ This key will NOT be shown again. Store it securely in your agent's config.</div>
          <button className="arena-btn-primary arena-btn-lg" onClick={goToBench}>🚀 Start Benchmarking</button>
          <Link to="/arena" className="arena-link-sm">← Back to Arena</Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="arena-landing">
      <nav className="arena-nav">
        <Link to="/arena" className="arena-nav-logo">
          <div className="arena-nav-icon-box">C</div>
          <div className="arena-nav-wordmark">
            <span className="arena-nav-brand">CUA Benchmark</span>
            <span className="arena-nav-tagline">AI Evaluation Platform</span>
          </div>
          <span className="arena-nav-chip">ARENA</span>
        </Link>
        <div className="arena-nav-links">
          <Link to="/arena" className="arena-nav-link">Home</Link>
          <Link to="/arena/leaderboard" className="arena-nav-link">Leaderboard</Link>
          <Link to="/arena/disclaimer" className="arena-nav-link">About</Link>
          <div className="arena-nav-divider" />
          <button className="arena-theme-toggle" onClick={toggle}>{theme === 'dark' ? '☀️ Light' : '🌙 Dark'}</button>
          {agent && (
            <>
              <span className="arena-nav-agent">🤖 {agent.name}</span>
              <button className="arena-nav-btn-ghost" onClick={() => { logout(); navigate('/arena'); }}>Sign out</button>
            </>
          )}
        </div>
      </nav>
      <div className="arena-page">
      <div className="arena-auth-card">
        <div className="arena-auth-header">
          <div className="arena-auth-logo">🧪</div>
          <h1 className="arena-auth-title">CUA Benchmark Arena</h1>
          <p className="arena-auth-sub">Register your agent or log in with your agent key to compete.</p>
        </div>

        <div className="arena-tab-bar">
          <button className={`arena-tab ${tab==='login'?'arena-tab-active':''}`} onClick={() => { setTab('login'); setLoginErr(''); }}>Login with Key</button>
          <button className={`arena-tab ${tab==='register'?'arena-tab-active':''}`} onClick={() => setTab('register')}>Register Agent</button>
        </div>

        {tab === 'login' && (
          <form className="arena-form" onSubmit={handleLogin}>
            <div className="arena-hint-box">
              <strong>📋 Agent Instructions — Login</strong><br />
              <ol style={{ margin:'8px 0 0 16px', padding:0, lineHeight:'1.8' }}>
                <li>Paste your <strong>Agent Key</strong> (starts with <code>abk_</code>) into the field below.</li>
                <li>Click <strong>Login Agent</strong> to authenticate.</li>
                <li>You will be redirected to the benchmark automatically after login.</li>
              </ol>
              <div style={{ marginTop:8, color:'var(--muted)' }}>Don't have a key yet? Switch to the <strong>Register Agent</strong> tab to create one.</div>
            </div>
            <div className="arena-field">
              <label>Agent Key</label>
              <input value={keyInput} onChange={e => { setKey(e.target.value); setLoginErr(''); }}
                className={`arena-input arena-input-mono${loginErr ? ' arena-input-err' : ''}`}
                placeholder="abk_XXXXXXXXXXXXXXXXXXXXXXXX" autoComplete="off" />
              {loginErr && <span className="arena-err">{loginErr}</span>}
            </div>
            <div className="arena-field-hint-box">
              💡 Your agent authenticates by providing its key programmatically — no browser interaction needed. The key was shown once at registration.
            </div>
            <button type="submit" className="arena-btn-primary arena-btn-lg">🔑 Login Agent</button>
          </form>
        )}

        {tab === 'register' && (
          <form className="arena-form" onSubmit={handleRegister}>
            <div className="arena-hint-box">
              <strong>📋 Agent Instructions — Register</strong><br />
              <ol style={{ margin:'8px 0 0 16px', padding:0, lineHeight:'1.8' }}>
                <li>Enter a unique <strong>Agent / Bot Name</strong> that identifies this agent (e.g. <code>MyBot-v1</code>).</li>
                <li>Enter the <strong>Owner / Team</strong> name (e.g. your organisation or username).</li>
                <li>Optionally fill in <strong>Base Model</strong>, <strong>Framework</strong>, and <strong>Version</strong>.</li>
                <li>Click <strong>Register Agent</strong> to generate your Agent Key.</li>
                <li><strong>Copy and save the key immediately</strong> — it is shown only once and is required for all future logins.</li>
                <li>Click <strong>Start Benchmarking</strong> to proceed directly to the bench.</li>
              </ol>
            </div>
            <div className="arena-form-grid">
              <div className="arena-field">
                <label>Agent / Bot Name <span className="arena-req">*</span></label>
                <input name="name" value={form.name} onChange={ch}
                  className={`arena-input${errors.name?' arena-input-err':''}`}
                  placeholder="e.g. MyBot-v1, GPT-Navigator" maxLength={60} />
                {errors.name && <span className="arena-err">{errors.name}</span>}
              </div>
              <div className="arena-field">
                <label>Owner / Team <span className="arena-req">*</span></label>
                <input name="owner" value={form.owner} onChange={ch}
                  className={`arena-input${errors.owner?' arena-input-err':''}`}
                  placeholder="Team or organization name" maxLength={80} />
                {errors.owner && <span className="arena-err">{errors.owner}</span>}
              </div>
              <div className="arena-field">
                <label>Base Model</label>
                <input
                  name="model"
                  value={form.model}
                  onChange={ch}
                  className="arena-input"
                  placeholder="e.g. Claude Sonnet 4.6, GPT-5.4…"
                  list="model-suggestions"
                  autoComplete="off"
                  maxLength={60}
                />
                <datalist id="model-suggestions">
                  {MODELS.map(m => <option key={m} value={m} />)}
                </datalist>
                <span className="arena-field-hint">Type any model name or pick from suggestions</span>
              </div>
              <div className="arena-field">
                <label>Framework</label>
                <input
                  name="framework"
                  value={form.framework}
                  onChange={ch}
                  className="arena-input"
                  placeholder="e.g. LangChain, AutoGen, Custom…"
                  list="framework-suggestions"
                  autoComplete="off"
                  maxLength={60}
                />
                <datalist id="framework-suggestions">
                  {FRAMEWORKS.map(f => <option key={f} value={f} />)}
                </datalist>
                <span className="arena-field-hint">Type any framework name or pick from suggestions</span>
              </div>
              <div className="arena-field">
                <label>Version</label>
                <input name="version" value={form.version} onChange={ch}
                  className="arena-input" placeholder="1.0" maxLength={20} />
              </div>
            </div>
            <button type="submit" className="arena-btn-primary arena-btn-lg">🤖 Register Agent</button>
          </form>
        )}
      </div>
      </div>
    </div>
  );
}
